import { useCallback, useEffect, useMemo, useState } from 'react';

import { Table } from '@components/common';

import useQueryConsistencyCheckExtraPathDetails from '@hooks/cmModule/consistencyCheckHistory/useQueryConsistencyCheckExtraPathDetails';
import useQueryConsistencyCheckExtraPathMoIds from '@hooks/cmModule/consistencyCheckHistory/useQueryConsistencyCheckExtraPathMoIds';
import useQueryConsistencyCheckExtraPathMoNames from '@hooks/cmModule/consistencyCheckHistory/useQueryConsistencyCheckExtraPathMoNames';

import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	ISortBy,
	ITablePagination,
	ITableSort,
	TTableFilter,
} from '@interfaces/components/table';

import { defaultMeta } from '@enums/common';

import { MIN_FILTER_WIDTH } from '@constants/common';
import { TableFilterTypes } from '@constants/table';

import { getColumns } from './utils';

import styles from './ConsistencyCheckHistoryExtraPathDetails.module.scss';

interface IConsistencyCheckHistoryExtraPathDetailsProps {
	dataModelPathId?: string;
	dumpConsistencyId?: string;
}

const ConsistencyCheckHistoryExtraPathDetails = ({
	dataModelPathId,
	dumpConsistencyId,
}: IConsistencyCheckHistoryExtraPathDetailsProps) => {
	const [page, setPage] = useState(defaultMeta.page);
	const [pageSize, setPageSize] = useState(defaultMeta.pageSize);
	const [sortDirection, setSortDirection] = useState(false);
	const [sortFieldName, setSortFieldName] = useState<string | null>(null);

	const [selectedManagedObjectId, setSelectedManagedObjectId] = useState<TSelectOption<string> | null>(null);
	const [selectedManagedObjectName, setSelectedManagedObjectName] = useState<TSelectOption<string> | null>(null);

	const isMoFilter = selectedManagedObjectId?.value || selectedManagedObjectName?.value;

	const {
		extraPathDetails,
		countOfRecords,
		isLoadingExtraPathDetails,
	} = useQueryConsistencyCheckExtraPathDetails({
		page,
		pageSize,
		sortDirection: sortFieldName ? sortDirection : undefined,
		sortFieldName,
		dataModelPathId: dataModelPathId || '',
		dumpConsistencyId: dumpConsistencyId || '',
		managedObjects: isMoFilter
			? [{
				moId: selectedManagedObjectId?.value,
				moName: selectedManagedObjectName?.value,
			}]
			: undefined,
	});

	const {
		moIds,
		isLoadingMoIds,
	} = useQueryConsistencyCheckExtraPathMoIds({
		dataModelPathId,
		dumpConsistencyId,
		moName: selectedManagedObjectName?.value,
	});

	const {
		moNames,
		isLoadingMoNames,
	} = useQueryConsistencyCheckExtraPathMoNames({
		dataModelPathId,
		dumpConsistencyId,
	});

	const pageHandler = useCallback((page: number) => setPage(page), []);
	const pageSizeHandler = useCallback((pageSize: number) => {
		setPageSize(pageSize);
		setPage(defaultMeta.page);
	}, []);

	const columns = useMemo(() => getColumns(), []);

	const pagination: ITablePagination = useMemo(() => ({
		countOfRecords: countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	}), [
		page,
		pageSize,
		countOfRecords,
		pageHandler,
		pageSizeHandler,
	]);

	const handleSort = useCallback((sortBy: ISortBy[]) => {
		setSortDirection(!!sortBy[0]?.desc);
		setSortFieldName(sortBy[0]?.id);
		setPage(defaultMeta.page);
	}, []);

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	}), [sortFieldName, sortDirection, handleSort]);

	const filters: TTableFilter[] = useMemo(() => {
		return [
			{
				name: 'managedObjects[0].moName',
				placeholder: 'Managed Object Name',
				option: selectedManagedObjectName,
				options: moNames,
				minWidth: MIN_FILTER_WIDTH,
				type: TableFilterTypes.SELECT,
				setState: setSelectedManagedObjectName,
				defaultValue: null,
			},
			{
				name: 'managedObjects[0].moId',
				placeholder: 'Managed Object ID',
				option: selectedManagedObjectId,
				options: moIds,
				minWidth: MIN_FILTER_WIDTH,
				disabled: !selectedManagedObjectName,
				type: TableFilterTypes.SELECT,
				setState: setSelectedManagedObjectId,
				defaultValue: null,
			},
		];
	}, [
		moNames,
		moIds,
		selectedManagedObjectId,
		selectedManagedObjectName,
		setSelectedManagedObjectName,
		setSelectedManagedObjectId,
	]);

	useEffect(() => {
		setSelectedManagedObjectId(null);
	}, [selectedManagedObjectName]);

	const isLoading = isLoadingExtraPathDetails
		|| isLoadingMoIds
		|| isLoadingMoNames;

	return (
		<div className={styles.wrapper}>
			<Table
				columns={columns}
				data={extraPathDetails}
				filters={filters}
				isDeleteActionDisabled
				isLoading={isLoading}
				isWithoutActions
				isWithoutCheckbox
				isWithoutDeleteAction
				pagination={pagination}
				sort={sort}
			/>
		</div>
	);
};

export default ConsistencyCheckHistoryExtraPathDetails;
